// shabba-dashboard/src/components/analytics/DepthImbalanceChart.tsx

import React from 'react';
import type { DepthImbalance } from '../../types';

interface FullBook {
  bidVolume: number;
  askVolume: number;
  imbalance: number;
  imbalancePercent: number;
}

interface DepthImbalanceChartProps {
  data: DepthImbalance;
  overall?: FullBook; // <-- NEW
}

export function DepthImbalanceChart({ data, overall }: DepthImbalanceChartProps) {
  const { bidVolume, askVolume, imbalance, imbalancePercent } = data;
  const totalVolume = bidVolume + askVolume;
  const bidPercentage = totalVolume > 0 ? (bidVolume / totalVolume) * 100 : 50;
  const askPercentage = totalVolume > 0 ? (askVolume / totalVolume) * 100 : 50;

  const getImbalanceColor = (pct = imbalancePercent) => {
    const absPercent = Math.abs(pct);
    if (absPercent > 30) return '#ef4444';
    if (absPercent > 15) return '#f59e0b';
    return '#10b981';
  };

  return (
    <div style={{ 
      backgroundColor: '#1e293b', 
      padding: '16px', 
      borderRadius: '8px', 
      border: '1px solid #334155' 
    }}>
      {/* NEW: Full Book Imbalance (unweighted) */}
      {overall && (
        <>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>
            Full Book Imbalance (Unweighted)
          </h4>

          {/* Bars */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: '#10b981' }}>Bids</span>
              <span style={{ fontSize: 11, color: '#ef4444' }}>Asks</span>
            </div>

            {(() => {
              const total = overall.bidVolume + overall.askVolume;
              const bPct = total > 0 ? (overall.bidVolume / total) * 100 : 50;
              const aPct = total > 0 ? (overall.askVolume / total) * 100 : 50;
              return (
                <div style={{ display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden', border: '1px solid #334155' }}>
                  <div style={{ width: `${bPct}%`, backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {bPct > 10 && (
                      <span style={{ fontSize: 10, color: 'white', fontWeight: 600 }}>
                        {overall.bidVolume.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div style={{ width: `${aPct}%`, backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {aPct > 10 && (
                      <span style={{ fontSize: 10, color: 'white', fontWeight: 600 }}>
                        {overall.askVolume.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Imbalance</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: getImbalanceColor(overall.imbalancePercent) }}>
                {overall.imbalancePercent > 0 ? '+' : ''}{overall.imbalancePercent.toFixed(1)}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>Total Volume</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>
                {(overall.bidVolume + overall.askVolume).toLocaleString()}
              </div>
            </div>
          </div>

          <div style={{ 
            marginTop: 4,
            marginBottom: 10,
            padding: '6px 8px',
            backgroundColor: getImbalanceColor(overall.imbalancePercent) + '20',
            borderRadius: 4,
            border: `1px solid ${getImbalanceColor(overall.imbalancePercent)}40`
          }}>
            <div style={{ 
              fontSize: 11,
              color: getImbalanceColor(overall.imbalancePercent),
              fontWeight: 600,
              textAlign: 'center'
            }}>
              {overall.imbalance > 0.1 ? 'BULLISH BIAS' :
               overall.imbalance < -0.1 ? 'BEARISH BIAS' : 'BALANCED'}
            </div>
          </div>

          <hr style={{ borderColor: '#334155', opacity: 0.5, margin: '8px 0 12px' }} />
        </>
      )}

      {/* Existing Top-N section */}
      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
        Top-{data.topN} Depth Imbalance
      </h4>
      
      {/* Volume Bars */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontSize: '11px', color: '#10b981' }}>Bids</span>
          <span style={{ fontSize: '11px', color: '#ef4444' }}>Asks</span>
        </div>
        
        <div style={{ 
          display: 'flex', 
          height: '20px', 
          borderRadius: '4px', 
          overflow: 'hidden',
          border: '1px solid #334155'
        }}>
          <div style={{
            width: `${bidPercentage}%`,
            backgroundColor: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {bidPercentage > 10 && (
              <span style={{ fontSize: '10px', color: 'white', fontWeight: '600' }}>
                {bidVolume.toLocaleString()}
              </span>
            )}
          </div>
          <div style={{
            width: `${askPercentage}%`,
            backgroundColor: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {askPercentage > 10 && (
              <span style={{ fontSize: '10px', color: 'white', fontWeight: '600' }}>
                {askVolume.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Imbalance Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Imbalance</div>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '700', 
            color: getImbalanceColor()
          }}>
            {imbalancePercent > 0 ? '+' : ''}{imbalancePercent.toFixed(1)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Total Volume</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#e2e8f0' }}>
            {totalVolume.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Imbalance Indicator */}
      <div style={{ 
        marginTop: '8px', 
        padding: '6px 8px', 
        backgroundColor: getImbalanceColor() + '20',
        borderRadius: '4px',
        border: `1px solid ${getImbalanceColor()}40`
      }}>
        <div style={{ 
          fontSize: '11px', 
          color: getImbalanceColor(),
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {imbalance > 0.1 ? 'BULLISH BIAS' : 
           imbalance < -0.1 ? 'BEARISH BIAS' : 'BALANCED'}
        </div>
      </div>
    </div>
  );
}
